import { useEffect, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { Bot, ShieldCheck, Eye, PenLine, AlertTriangle } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { apiService, DemandeConnecteur } from '@/services/api'

/* L'écran de consentement du connecteur MCP (28/10/2026) : Claude ou Copilot
   demande à agir dans UP-CRM au nom de la personne connectée. On dit
   clairement qui demande, où la réponse repart, et ce qui sera permis. */

export const CLE_RETOUR_CONNEXION = 'upcrm-retour-apres-connexion'

export default function ConnecteurAutoriser() {
  const { isAuthenticated, user } = useAuthStore()
  const [params] = useSearchParams()
  const demande = params.get('demande') ?? ''
  const [info, setInfo] = useState<DemandeConnecteur | null>(null)
  const [erreur, setErreur] = useState('')
  const [envoi, setEnvoi] = useState(false)

  /* Noté d'avance : si la session a expiré, l'appel ci-dessous renvoie à la
     connexion, qui ramènera ici. */
  const retenirRetour = () => {
    try { sessionStorage.setItem(CLE_RETOUR_CONNEXION, `/connecteur/autoriser?demande=${encodeURIComponent(demande)}`) } catch { /* navigation privée */ }
  }

  useEffect(() => {
    if (!isAuthenticated || !demande) return
    retenirRetour()
    void apiService.getDemandeConnecteur(demande).then((r) => {
      if (r.success && r.data) setInfo(r.data)
      else setErreur(r.error || 'Cette demande de connexion est introuvable.')
    })
  }, [isAuthenticated, demande])

  if (!isAuthenticated) {
    retenirRetour()
    return <Navigate to="/login" />
  }

  const decider = async (accepter: boolean) => {
    setEnvoi(true)
    const r = await apiService.deciderDemandeConnecteur(demande, accepter)
    try { sessionStorage.removeItem(CLE_RETOUR_CONNEXION) } catch { /* navigation privée */ }
    if (r.success && r.data) {
      window.location.href = r.data.redirection
    } else {
      setErreur(r.error || 'La réponse n’a pas pu être enregistrée.')
      setEnvoi(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <Card className="w-full max-w-lg">
        <CardContent className="p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center shrink-0">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Connecter un assistant IA à UP-CRM</h1>
              <p className="text-sm text-gray-500">Connecté en tant que {user?.username}</p>
            </div>
          </div>

          {erreur ? (
            <p className="text-sm text-red-600">{erreur}</p>
          ) : !info ? (
            <p className="text-sm text-gray-500">Chargement de la demande…</p>
          ) : (
            <>
              <p className="text-gray-700">
                <strong>{info.client}</strong> demande à accéder à UP-CRM en votre nom. La réponse repartira vers <strong>{info.hote_retour}</strong>.
              </p>

              {info.retour_local && (
                <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    La réponse repart vers une adresse de cet ordinateur. C’est normal si vous connectez Claude Code ou un autre outil installé ici ; sinon, refusez.
                  </span>
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900">L’assistant pourra :</p>
                <div className="flex gap-2 text-sm text-gray-700">
                  <Eye className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
                  <span>consulter les contacts et les entreprises, avec leurs champs personnalisés ;</span>
                </div>
                {info.ecriture ? (
                  <div className="flex gap-2 text-sm text-gray-700">
                    <PenLine className="w-4 h-4 mt-0.5 text-orange-600 shrink-0" />
                    <span>
                      créer, modifier et mettre à la corbeille des contacts et des entreprises, et restaurer des fiches de la corbeille. Il ne pourra pas supprimer définitivement. Chaque modification est inscrite au journal.
                    </span>
                  </div>
                ) : info.ecriture_demandee ? (
                  <p className="text-sm text-gray-500">
                    Il a aussi demandé à modifier des fiches, mais votre compte n’est pas administrateur : il n’aura que la lecture.
                  </p>
                ) : null}
              </div>

              <div className="flex gap-2 text-xs text-gray-500">
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <span>Vous pourrez retirer cet accès à tout moment, depuis l’assistant ou en le demandant à un administrateur d’UP-CRM.</span>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" disabled={envoi} onClick={() => void decider(false)}>Refuser</Button>
                <Button disabled={envoi} onClick={() => void decider(true)}>Autoriser</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
